import React, { useEffect, useState } from "react";
import { updateProfile } from "firebase/auth";
import { auth } from "../firebase";
import { usersApi } from "../services/api";
import { User as UserIcon, X, Loader2 } from "lucide-react";

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const ProfileDialog: React.FC<Props> = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState("");
  const [username, setUsername] = useState("");
  const [neighborhood, setNeighborhood] = useState("");
  const [neighborhoods, setNeighborhoods] = useState<string[]>([]);

  useEffect(() => {
    if (!isOpen) return;
    setError(null);
    setLoading(true);
    Promise.all([usersApi.me(), usersApi.neighborhoods()])
      .then(([m, n]) => {
        setDisplayName(m.user.displayName || "");
        setUsername(m.user.username || "");
        setNeighborhood(m.user.neighborhood || "");
        setNeighborhoods(n.neighborhoods || []);
      })
      .catch(e => setError(e?.message || "Could not load profile."))
      .finally(() => setLoading(false));
  }, [isOpen]);

  const save = async () => {
    setError(null);
    setSaving(true);
    try {
      const body: { displayName?: string; username?: string; neighborhood?: string } = {};
      const dn = displayName.trim();
      if (dn) body.displayName = dn;
      if (username.trim()) body.username = username.trim();
      if (neighborhood) body.neighborhood = neighborhood;
      await usersApi.updateMe(body);
      // Sync the Firebase Auth profile so Firestore-driven surfaces (chat, comments, leaderboard) pick up the new name.
      if (auth.currentUser && dn && dn !== auth.currentUser.displayName) {
        try { await updateProfile(auth.currentUser, { displayName: dn }); } catch {}
      }
      onClose();
    } catch (e: any) {
      setError(e?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-lg border border-[#d4a843]/40 bg-slate-950 p-5 shadow-xl" onClick={e => e.stopPropagation()}>
        <header className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2 text-[#d4a843]">
            <UserIcon size={18} />
            <h2 className="font-mono text-sm font-bold uppercase tracking-wide">Profile</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-200" aria-label="Close"><X size={18} /></button>
        </header>

        {loading ? (
          <div className="flex items-center gap-2 text-slate-400 py-6 justify-center">
            <Loader2 size={16} className="animate-spin" /> Loading…
          </div>
        ) : (
          <>
            <div className="mb-4">
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Display name</label>
              <input
                type="text"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                maxLength={40}
                placeholder="Your Facebook name (or whatever)"
                className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 focus:border-[#d4a843]/60 focus:outline-none"
              />
              <p className="text-[11px] text-slate-500 mt-1">Shown on your posts, photos, comments, and chat. We don&apos;t verify it.</p>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Handle / username</label>
              <input
                type="text"
                value={username}
                onChange={e => setUsername(e.target.value)}
                maxLength={20}
                placeholder="letters, numbers, underscore"
                className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 focus:border-[#d4a843]/60 focus:outline-none"
              />
              <p className="text-[11px] text-slate-500 mt-1">Shown as @handle next to your display name. Must be unique.</p>
            </div>

            <div className="mb-4">
              <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1">Neighborhood</label>
              <select
                value={neighborhood}
                onChange={e => setNeighborhood(e.target.value)}
                className="w-full rounded border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 focus:border-[#d4a843]/60 focus:outline-none"
              >
                <option value="">(not set)</option>
                {neighborhoods.map(n => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>

            {error && (
              <div className="mb-3 rounded border border-red-500/40 bg-red-950/40 px-3 py-2 text-xs text-red-300">{error}</div>
            )}

            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="px-3 py-2 rounded border border-slate-700 text-slate-300 text-xs font-bold uppercase tracking-wide hover:bg-slate-800">Cancel</button>
              <button
                onClick={save}
                disabled={saving}
                className="px-3 py-2 rounded border border-[#d4a843]/50 bg-[#d4a843]/20 text-[#d4a843] text-xs font-bold uppercase tracking-wide hover:bg-[#d4a843]/30 disabled:opacity-50 inline-flex items-center gap-2"
              >
                {saving && <Loader2 size={12} className="animate-spin" />}
                Save
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProfileDialog;
